// Extrusion helpers for the 3D preview.
//
// The 2D model is world millimetres, Y-up in plan. In three.js Y is the vertical
// axis, so we map:  three.x = worldX,  three.y = height (z),  three.z = -worldY.
// A 2D footprint ring (already in world plan coordinates) is extruded vertically
// between two heights with ExtrudeGeometry, then rotated so its plane lies flat.

import * as THREE from 'three';
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry.js';
import type { Vec2 } from '../../shared/types';

/** World plan point + height (mm) → three.js vector. */
export function to3(p: Vec2, z: number): THREE.Vector3 {
  return new THREE.Vector3(p.x, z, -p.y);
}

/**
 * A vertical prism from a world-plan polygon ring, spanning heights `z0`..`z1`.
 * The ring is taken in world plan coordinates (x = worldX, y = worldY). Returns
 * null when the ring is degenerate. `holes` are rings cut clean through it —
 * `THREE.Shape` carries them natively and ExtrudeGeometry normalises their
 * winding, so a rounded ring needs no rectangle decomposition.
 */
export function extrudePrism(ring: Vec2[], z0: number, z1: number, material: THREE.Material, holes?: Vec2[][]): THREE.Mesh | null {
  if (ring.length < 3) return null;
  const h = z1 - z0;
  if (Math.abs(h) < 1e-6) return null;
  const shape = new THREE.Shape(ring.map((p) => new THREE.Vector2(p.x, p.y)));
  for (const hole of holes ?? []) {
    if (hole.length >= 3) shape.holes.push(new THREE.Path(hole.map((p) => new THREE.Vector2(p.x, p.y))));
  }
  const geo = new THREE.ExtrudeGeometry(shape, { depth: Math.abs(h), bevelEnabled: false });
  // Shape lies in XY extruded along +Z; rotate so X→worldX, Y→worldZ(-), Z→height.
  geo.rotateX(-Math.PI / 2);
  geo.translate(0, Math.min(z0, z1), 0);
  geo.computeVertexNormals();
  return new THREE.Mesh(geo, material);
}

/** The convex hull of points (three.js frame), or null for a flat or too few. */
export function hullMesh(points: THREE.Vector3[], material: THREE.Material): THREE.Mesh | null {
  if (points.length < 4) return null;
  try {
    const geo = new ConvexGeometry(points);
    return geo.getAttribute('position')?.count ? new THREE.Mesh(geo, material) : null;
  } catch {
    return null;
  }
}

/** A rectangle (local box corners) as a CCW world ring via a local→world map. */
export function boxRing(box: { x0: number; y0: number; x1: number; y1: number }, toWorld: (p: Vec2) => Vec2): Vec2[] {
  return [
    toWorld({ x: box.x0, y: box.y0 }),
    toWorld({ x: box.x1, y: box.y0 }),
    toWorld({ x: box.x1, y: box.y1 }),
    toWorld({ x: box.x0, y: box.y1 }),
  ];
}

/** A planar face for `facesMesh`: a polygon with holes in its own 2D frame, `at`
 *  placing a frame point in the world (plan x, y and height z). */
export interface PlanarFace {
  contour: Vec2[];
  holes: Vec2[][];
  at: (p: Vec2) => { x: number; y: number; z: number };
  normal: { x: number; y: number; z: number };
}

/** ONE mesh from flat faces: each triangulated in its own frame (holes and all)
 *  and wound to face its `normal`, so coplanar surfaces are single faces and the
 *  pieces share their edges exactly. */
export function facesMesh(faces: PlanarFace[], material: THREE.Material): THREE.Mesh | null {
  const pos: number[] = [];
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), n = new THREE.Vector3();
  for (const f of faces) {
    if (f.contour.length < 3) continue;
    const contour = f.contour.map((p) => new THREE.Vector2(p.x, p.y));
    const holes = f.holes.filter((h) => h.length >= 3).map((h) => h.map((p) => new THREE.Vector2(p.x, p.y)));
    const verts = [...f.contour, ...holes.flatMap((h) => h.map((v) => ({ x: v.x, y: v.y })))].map((p) => { const q = f.at(p); return to3({ x: q.x, y: q.y }, q.z); });
    const want = to3({ x: f.normal.x, y: f.normal.y }, f.normal.z);
    for (const [i, j, k] of THREE.ShapeUtils.triangulateShape(contour, holes)) {
      a.copy(verts[i]); b.copy(verts[j]); c.copy(verts[k]);
      n.subVectors(b, a).cross(c.clone().sub(a));
      const [p1, p2] = n.dot(want) < 0 ? [c, b] : [b, c];
      pos.push(a.x, a.y, a.z, p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);
    }
  }
  if (!pos.length) return null;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.computeVertexNormals();
  return new THREE.Mesh(geo, material);
}
